import styled from 'styled-components';
import { FilterChipProps, QuickFilter } from '../../types';
import { useDispatch, useSelector } from 'react-redux';
import {
  SearchContext,
  SearchContextInterface
} from '../../../search-new/context/SearchContext';
import { MouseEvent, useCallback, useContext, useMemo } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'next-i18next';
import { logEvent } from '../../../../new-lib/analytics';
import { getPageLocation } from '../../../../helpers/common';
import { useRouter } from 'next/router';
import CloseIcon from '../../../../new-components/svg-icons/CloseIcon';
import useFilterActions from '../../hooks/FilterActions';
import {
  setDrawerOpen,
  setQuickFilter,
  setSelectedCompound
} from '../../../../redux/slices/mapSlice';

const FilterChip = ({ type, icon, label, ref }: FilterChipProps) => {
  const { asPath } = useRouter();

  const { t } = useTranslation('map');

  const dispatch = useDispatch();

  const { searchFiltersState, setShowFilters, appliedFiltersCount } =
    useContext(SearchContext) as SearchContextInterface;

  const selectedCompound = useSelector(
    (state: any) => state.map.selectedCompound
  );
  const { resetFilters } = useFilterActions();

  const appliedQuickFiltersCount = useMemo(() => {
    let count = 0;
    if (searchFiltersState.searchFilters.delivery_years.length > 0) {
      count++;
    }
    if (searchFiltersState.searchFilters.delivery_date) {
      count++;
    }
    if (searchFiltersState.searchFilters.property_types.length > 0) {
      count++;
    }
    if (searchFiltersState.searchFilters.min_price) {
      count++;
    }
    if (searchFiltersState.searchFilters.max_price) {
      count++;
    }

    return count;
  }, [appliedFiltersCount]);
  const isFilterSelected = useCallback(() => {
    if (
      (type === QuickFilter.NAWY_NOW &&
        searchFiltersState.searchFilters.nawy_now) ||
      (type === QuickFilter.PROPERTY_TYPE &&
        searchFiltersState.searchFilters.property_types.length > 0) ||
      (type === QuickFilter.DELIVERY &&
        (searchFiltersState.searchFilters.delivery_years.length > 0 ||
          searchFiltersState.searchFilters.delivery_date)) ||
      (type === QuickFilter.PRICE &&
        (searchFiltersState.searchFilters.min_price ||
          searchFiltersState.searchFilters.max_price)) ||
      (!type &&
        (searchFiltersState.searchFilters.developers.length > 0
          ? appliedFiltersCount - 1
          : appliedFiltersCount) -
          appliedQuickFiltersCount >
          0)
    ) {
      return true;
    } else {
      return false;
    }
  }, [
    searchFiltersState.searchFilters.property_types,
    searchFiltersState.searchFilters.delivery_years,
    searchFiltersState.searchFilters.delivery_date,
    searchFiltersState.searchFilters.max_price,
    searchFiltersState.searchFilters.min_price,
    searchFiltersState.searchFilters.nawy_now,
    appliedFiltersCount
  ]);

  const handleClick = () => {
    if (type) {
      if (isMobile && selectedCompound) {
        dispatch(setDrawerOpen(false));
        dispatch(setSelectedCompound(null));
      }
      dispatch(setQuickFilter(type));
      logEvent.search(
        `${type}_fast_filter_click`,
        getPageLocation(asPath),
        `map`,
        searchFiltersState
      );
    } else {
      setShowFilters(true);
    }
  };

  const handleResetFilter = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    resetFilters(type);
  };

  return (
    <Container
      onClick={handleClick}
      isSelected={isFilterSelected()}
      type={type}
      ref={ref}
    >
      {icon}

      <span>{t(label)}</span>

      {!type &&
        (searchFiltersState.searchFilters.developers.length > 0
          ? appliedFiltersCount - 1
          : appliedFiltersCount) -
          appliedQuickFiltersCount >
          0 && (
          <span className="filters-indicator">
            {appliedFiltersCount - appliedQuickFiltersCount}
          </span>
        )}

      {type &&
        isFilterSelected() &&
        !(
          type === QuickFilter.DELIVERY &&
          searchFiltersState.searchFilters.nawy_now
        ) && (
          <div className="close-btn" onClick={handleResetFilter}>
            <CloseIcon fill="#015C9A" styles={{ width: 10, height: 10 }} />
          </div>
        )}
    </Container>
  );
};

const Container = styled.div<{ isSelected: boolean; type: any }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 40px;
  padding: 6px 12px;
  border-radius: 360px;
  box-shadow:
    0px 0px 6px 0px rgba(0, 0, 0, 0.02),
    0px 2px 4px 0px rgba(0, 0, 0, 0.08);
  background-color: ${props =>
    props.type === 'nawy_now'
      ? props.isSelected
        ? '#FF5E00'
        : '#fff'
      : props.isSelected
        ? '#015C9A'
        : '#fff'};
  cursor: pointer;
  white-space: nowrap;
  text-overflow: ellipsis;
  border: ${props =>
    props.type === 'nawy_now' && !props.isSelected ? '1px solid #FF5E00' : ''};

  circle {
    fill: ${props => (props.isSelected ? '#fff' : '#697586')};
  }

  span {
    font-size: 13px;
    font-family: materialRegular;
    color: ${props =>
      props.type === 'nawy_now'
        ? props.isSelected
          ? '#fff'
          : '#FF5E00'
        : props.isSelected
          ? '#fff'
          : '#2C3138'};
  }

  .filters-indicator {
    font-size: 8px;
    color: ${props => props.theme.light.colors.white};
    background-color: ${props => props.theme.light.colors.primaryOrange};
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
  }

  svg {
    width: 20px;
    height: 20px;
    path {
      stroke: ${props => (props.isSelected ? '#fff' : '')};
      fill: ${props => props.isSelected && props.type === 'nawy_now' && '#fff'};
    }
  }

  .close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    width: 20px;
    height: 20px;
    border-radius: 360px;
    z-index: 99;
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    height: 29px;
    span {
      font-size: 14px;
      font-family: materialMedium;
    }
  }
`;

export default FilterChip;
